import OrderServices from "../services/orderServices";

export const INVOICE_PRINT_CONSTANTS = {
  PRINT_DELAY_MS: 800,
} as const;

export type InvoiceMediaIdentifier =
  | string
  | { $oid?: string; oid?: string }
  | null
  | undefined;

export type InvoicePrintErrorCode =
  | "NO_INVOICES"
  | "MERGE_FAILED"
  | "POPUP_BLOCKED"
  | "INVALID_RESPONSE";

export class InvoicePrintError extends Error {
  code: InvoicePrintErrorCode;
  constructor(message: string, code: InvoicePrintErrorCode) {
    super(message);
    this.name = "InvoicePrintError";
    this.code = code;
  }
}

export const normalizeInvoiceMediaId = (
  invoiceMediaId: InvoiceMediaIdentifier
): string | null => {
  if (!invoiceMediaId) return null;
  if (typeof invoiceMediaId === "string") {
    const trimmed = invoiceMediaId.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof invoiceMediaId === "object") {
    if (typeof invoiceMediaId.$oid === "string") return invoiceMediaId.$oid;
    if (typeof invoiceMediaId.oid === "string") return invoiceMediaId.oid;
  }
  return null;
};

export const dedupeInvoiceMediaIds = (
  rawMediaIds: InvoiceMediaIdentifier[]
): string[] => {
  const normalizedIds = rawMediaIds
    .map(normalizeInvoiceMediaId)
    .filter((v): v is string => Boolean(v));
  return Array.from(new Set(normalizedIds));
};

const openBlobInNewTab = (
  blob: Blob,
  autoPrint = false,
  printDelayMs = INVOICE_PRINT_CONSTANTS.PRINT_DELAY_MS
): Window | null => {
  const blobUrl = URL.createObjectURL(blob);
  const w = window.open(blobUrl, "_blank");
  if (!w) {
    URL.revokeObjectURL(blobUrl);
    return null;
  }

  const cleanup = () => {
    try {
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
    }
  };

  if (autoPrint) {
    setTimeout(() => {
      try {
        w.focus();
        w.print?.();
      } catch (err) {
      }
    }, printDelayMs);
  }

  try {
    w.addEventListener?.("beforeunload", cleanup);
    w.addEventListener?.("unload", cleanup);
  } catch {
  }

  setTimeout(() => {
    try {
      if (w.closed) {
        cleanup();
      }
    } catch {
    }
  }, 60000);

  return w;
};

export type PrintOptions = {
  autoPrint?: boolean;
};

export const printGaraazInvoices = async (
  rawMediaIds: InvoiceMediaIdentifier[],
  opts: PrintOptions = {
    autoPrint: true,
  }
): Promise<{
  printableCount: number;
  attemptedCount: number;
}> => {
  const { autoPrint = false } = opts;

  const mediaIds = dedupeInvoiceMediaIds(rawMediaIds);
  if (mediaIds.length === 0) {
    throw new InvoicePrintError(
      "No Garaaz invoices available to print.",
      "NO_INVOICES"
    );
  }

  try {
    const mergedPdfBlob = await OrderServices.mergeInvoices(mediaIds);

    if (!mergedPdfBlob || mergedPdfBlob.size === 0) {
      throw new InvoicePrintError(
        "Invalid response from server: empty PDF received.",
        "INVALID_RESPONSE"
      );
    }

    const popup = openBlobInNewTab(mergedPdfBlob, autoPrint);

    if (!popup) {
      throw new InvoicePrintError(
        "Unable to open invoices because the pop-up was blocked. Please allow pop-ups for this site.",
        "POPUP_BLOCKED"
      );
    }

    return {
      printableCount: mediaIds.length,
      attemptedCount: mediaIds.length,
    };
  } catch (err) {
    if (err instanceof InvoicePrintError) {
      throw err;
    }

    if (err instanceof Error) {
      if (
        err.message.includes("merge") ||
        err.message.includes("Merge") ||
        err.message.includes("MERGE")
      ) {
        throw new InvoicePrintError(err.message, "MERGE_FAILED");
      }

      throw new InvoicePrintError(
        err.message ||
          "Failed to merge and print invoices. Please try again later.",
        "MERGE_FAILED"
      );
    }

    throw new InvoicePrintError(
      "Failed to merge and print invoices. Please try again later.",
      "MERGE_FAILED"
    );
  }
};
